import React from 'react'
import { motion, useAnimation, useInView } from 'framer-motion'
import { useTheme } from './ThemeContext'

const Journey = () => {
  const controls = useAnimation()
  const ref = React.useRef(null)
  const isInView = useInView(ref, { once: true, amount: 0.3 })
  const { isDarkMode } = useTheme()

  React.useEffect(() => {
    if (isInView) {
      controls.start('visible')
    }
  }, [controls, isInView])

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        when: "beforeChildren"
      },
    },
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
      },
    },
  }

  return (
    <motion.section 
      id="journey" 
      className={`py-24 ${isDarkMode ? 'bg-gray-800' : 'bg-gray-100'}`}
      ref={ref}
      initial="hidden"
      animate={controls}
      variants={containerVariants}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div 
          className="md:flex md:items-center md:justify-between"
          variants={containerVariants}
        >
          <motion.div 
            className="md:w-1/2 md:pr-12"
            variants={itemVariants}
          >
            <h2 className={`text-4xl font-bold mb-6 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>About Me</h2>
            <p className={`${isDarkMode ? 'text-gray-300' : 'text-gray-600'} mb-4`}>
              I&apos;m an AI systems builder, full-stack engineer, and K-8 STEM teacher. My work focuses on practical,
              reliable products that help people create, learn, and solve real problems.
            </p>
            <p className={isDarkMode ? 'text-gray-300' : 'text-gray-600'}>
              I came into software by building for real people first through freelance web work, then through
              products for my students. That path made me practical: I care about shipping, iteration, safety,
              and making complex systems genuinely useful.
            </p>
          </motion.div>
          <motion.div 
            className="md:w-1/2 mt-12 md:mt-0"
            variants={itemVariants}
          >
            <motion.div 
              className="bg-teal-500 text-white p-8 rounded-lg shadow-xl"
              whileHover={{ scale: 1.05 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              <h3 className="text-2xl font-bold mb-4">Focus Areas</h3>
              <ul className="space-y-2">
                <motion.li variants={itemVariants}>• AI Systems and Agent Workflows</motion.li>
                <motion.li variants={itemVariants}>• Full-stack Product Development</motion.li>
                <motion.li variants={itemVariants}>• Evaluation, Reliability, and Safety</motion.li>
                <motion.li variants={itemVariants}>• Education Technology and Classroom Tools</motion.li>
                <motion.li variants={itemVariants}>• Human-centered Product Design</motion.li>
              </ul>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>
    </motion.section>
  )
}

export default Journey
